import React from 'react';
import { Hazard, HazardStatus, HazardSeverity } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface HazardsTableProps {
  hazards: Hazard[];
  loading: boolean;
  onSelect: (hazard: Hazard) => void;
  onRefresh: () => void;
}

export const HazardsTable: React.FC<HazardsTableProps> = ({
  hazards,
  loading,
  onSelect,
}) => {
  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(hazards);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);
  const getStatusBadge = (status: HazardStatus) => {
    switch (status) {
      case 'DRAFT':
        return <span className="badge-pill badge-neutral">Draft</span>;
      case 'IDENTIFIED':
        return <span className="badge-pill badge-info">Identified</span>;
      case 'UNDER_REVIEW':
        return <span className="badge-pill badge-warning">Under Review</span>;
      case 'ACTIVE':
        return <span className="badge-pill badge-danger">Active</span>;
      case 'MITIGATED':
      case 'ACCEPTED':
        return <span className="badge-pill badge-success">{status.replace('_', ' ')}</span>;
      case 'RETIRED':
      case 'CLOSED':
        return <span className="badge-pill badge-neutral">{status.replace('_', ' ')}</span>;
      default:
        return <span className="badge-pill badge-neutral">{status}</span>;
    }
  };

  const getSeverityBadge = (severity: HazardSeverity) => {
    switch (severity) {
      case 'CRITICAL':
        return <span className="badge-pill badge-danger">Critical</span>;
      case 'HIGH':
        return <span className="badge-pill badge-warning">High</span>;
      case 'MEDIUM':
        return <span className="badge-pill badge-info">Medium</span>;
      case 'LOW':
        return <span className="badge-pill badge-success">Low</span>;
      default:
        return <span className="badge-pill badge-neutral">{severity}</span>;
    }
  };

  const isOverdue = (nextReviewDate?: string, status?: HazardStatus) => {
    if (!nextReviewDate || status === 'RETIRED' || status === 'CLOSED') return false;
    return new Date(nextReviewDate) < new Date();
  };

  if (!loading && hazards.length === 0) {
    return (
      <div className="card" style={{ padding: '36px', textAlign: 'center', color: '#94a3b8' }}>
        No hazard records found matching filter criteria.
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="hazardNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Hazard No
              </SortableTh>
              <SortableTh field="categoryCode" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Category
              </SortableTh>
              <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Hazard Title & Statement
              </SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Status
              </SortableTh>
              <SortableTh field="initialSeverity" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Severity & Risk
              </SortableTh>
              <SortableTh field="nextReviewDate" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Next Review
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((h) => {
              const overdue = isOverdue(h.nextReviewDate, h.status);
              return (
                <tr
                  key={h.id}
                  onClick={() => onSelect(h)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontWeight: 700, color: '#0284c7', fontFamily: 'monospace' }}>
                    {h.hazardNumber}
                  </td>
                  <td>
                    <span className="badge-pill badge-neutral">
                      {h.categoryCode || 'GEN'}
                    </span>
                  </td>
                  <td style={{ maxWidth: '380px' }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: '2px' }}>{h.title}</div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#64748b',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <strong style={{ color: '#475569' }}>Cond:</strong> {h.conditionText}
                    </div>
                  </td>
                  <td>{getStatusBadge(h.status)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {getSeverityBadge(h.severityEstimate)}
                      {h.initialRiskIndex && (
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: '#e0e7ff',
                            color: '#3730a3',
                          }}
                        >
                          {h.initialRiskIndex}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {h.nextReviewDate ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '12px', color: overdue ? '#dc2626' : '#475569', fontWeight: overdue ? 700 : 400 }}>
                          {new Date(h.nextReviewDate).toLocaleDateString()}
                        </span>
                        {overdue && (
                          <span className="badge-pill badge-danger" style={{ fontSize: '10px' }}>
                            OVERDUE
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(h);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                    >
                      Dossier
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="hazards"
      />
    </div>
  );
};
